import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { PageContent } from '../components/Layout'
import { API_BASE_URL } from '../utils/api'

interface EventType {
  id?: string
  name: string
  color: string
  is_default: boolean
  description?: string
}

const CalendarSettings: React.FC = () => {
  const { t } = useTranslation()
  const [eventTypes, setEventTypes] = useState<EventType[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [newEventType, setNewEventType] = useState<EventType>({
    name: '',
    color: '#3B82F6',
    is_default: false,
    description: ''
  })

  // Default event types that cannot be deleted
  const defaultEventTypes: EventType[] = [
    { name: 'Custom', color: '#6B7280', is_default: true, description: 'Custom calendar events' },
    { name: 'Appointment', color: '#F59E0B', is_default: true, description: 'Customer appointments' },
    { name: 'Deal', color: '#10B981', is_default: true, description: 'Deal-related events' },
    { name: 'Task', color: '#8B5CF6', is_default: true, description: 'Task with specific times' },
    { name: 'Lead', color: '#EF4444', is_default: true, description: 'Lead follow-ups' }
  ]

  useEffect(() => {
    fetchEventTypes()
  }, [])

  const fetchEventTypes = async () => {
    try {
      setLoading(true)
      // For now, we'll use the default types and any custom ones from localStorage
      const customTypes = JSON.parse(localStorage.getItem('customEventTypes') || '[]')
      setEventTypes([...defaultEventTypes, ...customTypes])
    } catch (error) {
      console.error('Error fetching event types:', error)
      setEventTypes(defaultEventTypes)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateEventType = async () => {
    if (!newEventType.name.trim()) return

    try {
      // For now, save to localStorage (in the future, this would be an API call)
      const customTypes = JSON.parse(localStorage.getItem('customEventTypes') || '[]')
      const eventType: EventType = {
        id: Date.now().toString(),
        ...newEventType,
        is_default: false
      }
      
      customTypes.push(eventType)
      localStorage.setItem('customEventTypes', JSON.stringify(customTypes))
      
      setEventTypes([...defaultEventTypes, ...customTypes])
      setNewEventType({ name: '', color: '#3B82F6', is_default: false, description: '' })
      setShowCreateModal(false)
    } catch (error) {
      console.error('Error creating event type:', error)
      alert('Failed to create event type')
    }
  }

  const handleDeleteEventType = async (eventTypeId: string) => {
    try {
      const customTypes = JSON.parse(localStorage.getItem('customEventTypes') || '[]')
      const updatedTypes = customTypes.filter((type: EventType) => type.id !== eventTypeId)
      localStorage.setItem('customEventTypes', JSON.stringify(updatedTypes))
      
      setEventTypes([...defaultEventTypes, ...updatedTypes])
    } catch (error) {
      console.error('Error deleting event type:', error)
      alert('Failed to delete event type')
    }
  }

  const colorOptions = [
    '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', 
    '#6B7280', '#EC4899', '#14B8A6', '#F97316', '#84CC16'
  ]

  return (
    <PageContent>
      {/* Page Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('calendar.settings.title', 'Calendar Settings')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              {t('calendar.settings.subtitle', 'Manage your calendar event types and preferences')}
            </p>
          </div>
          
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors duration-200 font-medium shadow-sm flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span>Add Event Type</span>
          </button>
        </div>
      </div>

      {/* Event Types Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Event Types
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : (
          <div className="space-y-3">
            {eventTypes.map((eventType, index) => (
              <div
                key={eventType.id || index}
                className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div
                    className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                    style={{ backgroundColor: eventType.color }}
                  />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {eventType.name}
                      {eventType.is_default && (
                        <span className="ml-2 inline-flex px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full">
                          Default
                        </span>
                      )}
                    </h3>
                    {eventType.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {eventType.description}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  {!eventType.is_default && (
                    <button
                      onClick={() => eventType.id && handleDeleteEventType(eventType.id)}
                      className="px-3 py-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                    >
                      Delete
                    </button>
                  )}
                  {eventType.is_default && (
                    <span className="px-3 py-2 text-gray-400 text-sm">
                      Cannot be modified
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Event Type Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create New Event Type
              </h2>
              <button
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                onClick={() => setShowCreateModal(false)}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Event Type Name *
                </label>
                <input
                  type="text"
                  value={newEventType.name}
                  onChange={(e) => setNewEventType({...newEventType, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  placeholder="Enter event type name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color
                </label>
                <div className="grid grid-cols-5 gap-2">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      onClick={() => setNewEventType({...newEventType, color})}
                      className={`w-10 h-10 rounded-lg border-2 ${
                        newEventType.color === color 
                          ? 'border-gray-900 dark:border-gray-100' 
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Description
                </label>
                <textarea
                  value={newEventType.description}
                  onChange={(e) => setNewEventType({...newEventType, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                  placeholder="Enter description (optional)"
                />
              </div>
            </div>
            
            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateEventType}
                disabled={!newEventType.name.trim()}
                className="px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Event Type
              </button>
            </div>
          </div>
        </div>
      )}
    </PageContent>
  )
}

export default CalendarSettings
